"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import { supabase } from "@/lib/supabase";
import { hasStoredSupabaseSession } from "@/lib/auth/session-cookie-storage";
import { storage } from "@/lib/storage";
import { useQueryClient } from "@tanstack/react-query";
import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { QUERY_CACHE_KEY } from "@/contexts/QueryProvider";
import { resolveApiRequestUrl } from "@/lib/routing/api-requests";
import { authHref } from "@/lib/routing/platform-hosts";
import { useAuthStore, writeCache, clearCache, type Profile } from "@/stores/auth-store";
import { isTransientConnectivityError, withDeadline } from "@/lib/query-resilience";
import { useOffline } from "@/contexts/OfflineProvider";
import { clearAccessSnapshot } from "@/lib/access-snapshot";
import { useSessionRenewal } from "@/hooks/useSessionRenewal";

// ====================
// Types
// ====================
interface AuthContextProps {
  user: any;
  session: any;
  profile: Profile | null;
  loading: boolean;
  initialized: boolean;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  updateProfile: (updates: {
    full_name?: string;
    avatar_url?: string;
    role?: string;
  }) => Promise<{ error?: string }>;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (
    email: string,
    password: string,
    fullName: string,
    role?: "user" | "admin"
  ) => Promise<{ error?: string; errorCode?: string }>;
  signInWithGoogle: () => Promise<{ error?: string }>;
  signInWithApple: () => Promise<{ error?: string }>;
  ensureSession: () => Promise<any>;
}

const AuthContext = createContext<AuthContextProps | null>(null);

const PROFILE_MISSING_ERROR_MESSAGE =
  "Your account profile is not ready yet. Please try signing in again before starting onboarding.";
const PROFILE_LOAD_ERROR_MESSAGE =
  "We could not load your account profile. Please try again before continuing onboarding.";
const PROFILE_CACHE_TTL_MS = 60_000;

/**
 * How long `fetchProfile` may wait on `supabase.from('profiles')` before giving up.
 *
 * `createTimedFetch` bounds the HTTP request at `SUPABASE_REQUEST_TIMEOUT_MS`
 * (20s), but it cannot bound `getSession()` queued behind the auth lock, which
 * waits forever (see `withDeadline`). This sits below the transport timeout so
 * the profile read always settles first.
 *
 * Cold start already has an equivalent guard , the 8s `initAuth` watchdog , but
 * that covers `initAuth` only. The interactive sign-in had nothing: `handleSubmit`
 * awaited `signIn()` with no bound, so a stalled profile read froze the form on
 * "Signing in…" with no error and no way to retry.
 *
 * On expiry the read rejects as a transient connectivity error, so `signIn`
 * returns its existing "signed in, but we could not load your profile" outcome:
 * the session is already established and revalidates in the background.
 */
const PROFILE_LOAD_DEADLINE_MS = 12_000;

/**
 * How long after the last confirmed-good session we keep trusting the cached
 * session when Supabase hands us a `null` one without an explicit sign-out.
 *
 * This used to be 30s, which is shorter than a single request timeout (20s) plus
 * one retry , so "close the laptop, come back, the first refresh is slow" landed
 * outside the window and logged the user out. A session cookie is valid for 30
 * days; there is no reason for the client to give up on it in half a minute.
 */
const SESSION_GRACE_MS = 24 * 60 * 60 * 1000;

/**
 * Events where Supabase is telling us, definitively, that the session is gone.
 * Every other `null` session is a failed refresh, and a failed refresh is not a
 * sign-out , auth-js only drops the stored session on a non-retryable error, and
 * when it does it emits SIGNED_OUT.
 */
const TERMINAL_AUTH_EVENTS = new Set(["SIGNED_OUT", "USER_DELETED"]);

/**
 * Is there still a stored session token for us to refresh from?
 *
 * This is what separates "the refresh has not succeeded yet" from "there is no
 * session any more". `getSession()` returning null tells you neither on its own,
 * which is why the grace-period heuristics above must never be the only guard:
 * with a token still on disk we wait indefinitely, and with no token we stop
 * pretending the user is signed in.
 */
function hasStoredSession() {
  try {
    return hasStoredSupabaseSession();
  } catch {
    // Storage unavailable , assume a session exists rather than evicting.
    return true;
  }
}

const getAuthErrorMessage = (error: unknown, fallback: string) => {
  if (error instanceof Error && error.message) return error.message;
  if (typeof error === "string" && error.trim()) return error;
  return fallback;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// ====================
// AuthProvider
// ====================
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const store = useAuthStore();
  const queryClient = useQueryClient();
  const storeRef = useRef(store);
  // Prevent concurrent profile fetches
  const fetchingProfile = useRef(false);
  const profileRequestRef = useRef<{ userId: string; promise: Promise<Profile | null> } | null>(null);
  const profileFetchedAtRef = useRef(0);
  // Tracks the last time we had a confirmed valid session.  When a network
  // failure causes getSession() to return null, we use this to decide whether
  // to wait (recently valid → probably a blip) or clear (not recently valid →
  // probably a real logout).
  //
  // Seeded from the cached user rather than 0: the ref is recreated on every
  // page load, so starting at 0 meant the grace period protected *only* long
  // -lived tabs and never a cold start , which is exactly the "came back after a
  // while, got logged out" case, because coming back always means a fresh load.
  const lastValidSessionAtRef = useRef(store.user ? Date.now() : 0);

  storeRef.current = store;

  useRealtimeInvalidate("profiles", ["profile", store.user?.id], undefined, {
    enabled: Boolean(store.user?.id),
    filter: store.user?.id ? `id=eq.${store.user.id}` : undefined,
  });

  // Mirror the SDK's fresh access token into the httpOnly access cookie so the
  // edge gate always finds a live token (09a #4). Never rotates refresh tokens.
  useSessionRenewal(Boolean(store.user?.id));

  // Register sync handler for auth actions queued while offline
  const { registerSyncHandler } = useOffline();
  useEffect(() => {
    const unregister = registerSyncHandler('auth', async (action) => {
      if (action.type === 'update_profile') {
        const { error } = await supabase
          .from('profiles')
          .update(action.payload)
          .eq('id', action.metadata?.userId as string);
        return !error;
      }
      return false;
    });
    return unregister;
  }, [registerSyncHandler]);

  const clearUserScopedQueryData = useCallback(() => {
    queryClient.clear();
    localStorage.removeItem(QUERY_CACHE_KEY);
    // The signed access snapshot is scoped to (user, business) — it must not
    // survive a sign-out, or the next account on this device would inherit the
    // previous account's cached entitlements until the first refresh.
    clearAccessSnapshot();
  }, [queryClient]);

  const clearAuthState = useCallback((options?: { clearQueries?: boolean }) => {
    storeRef.current.reset();
    profileFetchedAtRef.current = 0;
    profileRequestRef.current = null;
    if (options?.clearQueries) clearUserScopedQueryData();
  }, [clearUserScopedQueryData]);

  const fetchProfile = useCallback(async (
    userId: string,
    userObj: any,
    sessionObj?: any
  ): Promise<Profile | null> => {
    if (!userId) {
      storeRef.current.setProfile(null);
      return null;
    }

    const now = Date.now();
    if (
      storeRef.current.profile?.id === userId &&
      now - profileFetchedAtRef.current < PROFILE_CACHE_TTL_MS
    ) {
      return storeRef.current.profile;
    }

    if (profileRequestRef.current?.userId === userId) {
      return profileRequestRef.current.promise;
    }

    const promise = (async () => {
      // Bounded: `getSession()` inside this query can queue behind a held auth
      // lock and never settle, which would strand every caller's `finally` ,
      // the disabled sign-in form included. See `PROFILE_LOAD_DEADLINE_MS`.
      const { data, error } = await withDeadline(
        supabase
          .from("profiles")
          .select("*")
          .eq("id", userId)
          .maybeSingle(),
        PROFILE_LOAD_DEADLINE_MS
      );

      if (error) throw new Error(`${PROFILE_LOAD_ERROR_MESSAGE} ${error.message}`);
      if (!data) throw new Error(PROFILE_MISSING_ERROR_MESSAGE);

      const userProfile: Profile = {
        ...data,
        id: userId,
        email: data.email || userObj.email || "",
        role: data.role || "user",
      };

      profileFetchedAtRef.current = Date.now();
      storeRef.current.setProfile(userProfile);
      writeCache({
        user: userObj,
        profile: userProfile,
      });
      queryClient.setQueryData(["profile", userId], userProfile);
      return userProfile;
    })().finally(() => {
      if (profileRequestRef.current?.userId === userId) {
        profileRequestRef.current = null;
      }
    });

    profileRequestRef.current = { userId, promise };
    return promise;
  }, [queryClient]);

  useEffect(() => {
    let ignore = false;

    // Watchdog: `initialized` must become true no matter what happens below.
    //
    // `initAuth` awaits `getSession()` and then `fetchProfile()`. Neither has a
    // timeout, and `ProtectedRoute` renders its full-page "Verifying access" card
    // whenever `initialized` is false , with no retry, no redirect and no escape,
    // because its own effect returns early on `!initialized`. So a single hung
    // request during cold start did not degrade the app, it removed it: a merchant
    // clicking into Team sat on a skeleton indefinitely.
    //
    // Proceeding on a timeout is safe and is what the store was designed for: it is
    // already seeded from the cached session, so the UI renders from cache and the
    // real session revalidates in the background when the network returns.
    const watchdog = setTimeout(() => {
      if (ignore) return;
      if (!storeRef.current.initialized) {
        console.warn('[auth] initialisation did not settle in time , continuing from cached state');
        storeRef.current.setInitialized(true);
      }
    }, 8000);

    const initAuth = async () => {
      // Silently validate the cached session in the background.
      // The store is already seeded from cache so the UI renders immediately.
      const result = await supabase.auth.getSession().catch(() => ({ data: { session: null }, error: null }));
      if (ignore) return;

      const freshSession = result?.data?.session ?? null;
      const cachedUserId = storeRef.current.user?.id ?? null;
      const freshUserId = freshSession?.user?.id ?? null;

      // Different user logged in elsewhere , clear everything.
      if (cachedUserId && cachedUserId !== freshUserId) {
        clearAuthState({ clearQueries: true });
        storeRef.current.setInitialized(true);
        return;
      }

      if (freshSession?.user) {
        // Session is confirmed good. Record that *before* touching the profile ,
        // the profile is derived data, and its fetch failing says nothing about
        // whether the user is signed in.
        if (!ignore) {
          storeRef.current.setSession(freshSession);
          storeRef.current.setUser(freshSession.user);
          lastValidSessionAtRef.current = Date.now();
        }

        // Only re-fetch profile if we don't have one or user changed.
        if (!storeRef.current.profile || storeRef.current.profile.id !== freshUserId) {
          if (!fetchingProfile.current) {
            fetchingProfile.current = true;
            try {
              await fetchProfile(freshSession.user.id, freshSession.user, freshSession);
            } catch (err) {
              // A failed profile read is never a reason to sign the user out.
              // We hold a valid session; the profile revalidates on the next
              // focus, realtime event or query.
              console.warn('[auth] profile revalidation failed , keeping session', err);
            } finally {
              fetchingProfile.current = false;
            }
          }
        }
      } else if (cachedUserId) {
        // getSession() returned null for a previously-authenticated user.
        // This is almost always a failed token refresh on a spotty connection,
        // NOT a real sign-out , auth-js keeps the stored session on retryable
        // failures and emits SIGNED_OUT when it genuinely drops it, which the
        // onAuthStateChange handler below acts on. Here we only ever wait.
        const networkDown = typeof navigator !== 'undefined' && !navigator.onLine;
        const recentlyValid = Date.now() - lastValidSessionAtRef.current < SESSION_GRACE_MS;
        if (!hasStoredSession()) {
          // Nothing left to refresh from , the session really is gone.
          clearAuthState({ clearQueries: true });
        } else if (!networkDown && !recentlyValid) {
          clearAuthState({ clearQueries: true });
        }
      }

      if (!ignore) {
        clearTimeout(watchdog);
        storeRef.current.setInitialized(true);
      }
    };

    initAuth();

    // Sync auth state across tabs.
    //
    // Supabase runs this callback *while holding* the auth lock
    // (navigator.locks "lock:sb-<ref>-auth-token"), and it awaits the callback
    // before releasing it.  Anything in here that touches Supabase ,
    // fetchProfile does supabase.from('profiles'), which calls getSession() to
    // pick up the access token , queues on that same lock and therefore waits
    // on itself.  The lock has no acquire timeout, so the deadlock is permanent:
    // no request is ever sent and the app hangs on "Verifying access" forever.
    // Handing the work to a macrotask lets the lock release first.
    const handleAuthStateChange = async (event: string, newSession: any) => {
      if (ignore) return;

      const cachedUserId = storeRef.current.user?.id ?? null;
      const newUserId = newSession?.user?.id ?? null;

      if (cachedUserId && newUserId && cachedUserId !== newUserId) {
        clearAuthState({ clearQueries: true });
      }

      if (newSession?.user) {
        // Adopt the session first. Previously the store was only updated *after*
        // fetchProfile() resolved, so a slow or failing profile read left the app
        // holding a stale session , and the catch below then cleared auth outright.
        if (!ignore) {
          storeRef.current.setSession(newSession);
          storeRef.current.setUser(newSession.user);
          storeRef.current.setInitialized(true);
          lastValidSessionAtRef.current = Date.now();
        }

        if (!fetchingProfile.current) {
          fetchingProfile.current = true;
          try {
            await fetchProfile(newSession.user.id, newSession.user, newSession);
          } catch (err) {
            // Profile reads fail for reasons that have nothing to do with the
            // session (timeout, 5xx, RLS hiccup). Keep the user signed in.
            console.warn('[auth] profile fetch failed after auth change , keeping session', err);
          } finally {
            fetchingProfile.current = false;
          }
        }
        return;
      }

      // Session is null. Only an explicit terminal event means "signed out";
      // TOKEN_REFRESHED failures, INITIAL_SESSION on a cold offline start and
      // every other null delivery are transient and must not evict the user.
      if (TERMINAL_AUTH_EVENTS.has(event) || !hasStoredSession()) {
        clearAuthState({ clearQueries: Boolean(cachedUserId) });
      }
      storeRef.current.setInitialized(true);
    };

    const { data: listener } = supabase.auth.onAuthStateChange((event, newSession) => {
      if (ignore) return;
      setTimeout(() => { void handleAuthStateChange(event, newSession); }, 0);
    });

    // Revalidate on tab focus , silently, no loading state shown.
    // Online-gated: if the network is down, skip the session check to avoid
    // a transient network blip clearing the user's auth state (reported as
    // "randomly logged out" when returning to a busy environment).
    let visibilityDebounceTimer: ReturnType<typeof setTimeout> | null = null;
    const handleVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      if (visibilityDebounceTimer) clearTimeout(visibilityDebounceTimer);
      visibilityDebounceTimer = setTimeout(async () => {
        if (ignore) return;
        if (typeof navigator !== "undefined" && !navigator.onLine) return;

        const { data } = await supabase.auth.getSession().catch(() => ({ data: { session: null } }));
        if (ignore) return;

        const liveUserId = data.session?.user?.id ?? null;
        const cachedUserId = storeRef.current.user?.id ?? null;

        // Only a *different* live user means "someone else signed in here".
        // A null live user means the refresh has not landed yet, which is the
        // normal state for the first few hundred ms after a long sleep.
        if (cachedUserId && liveUserId && cachedUserId !== liveUserId) {
          clearAuthState({ clearQueries: true });
          return;
        }

        if (data.session?.user) {
          if (!ignore) {
            storeRef.current.setSession(data.session);
            storeRef.current.setUser(data.session.user);
            lastValidSessionAtRef.current = Date.now();
          }

          if (!fetchingProfile.current) {
            fetchingProfile.current = true;
            try {
              await fetchProfile(data.session.user.id, data.session.user, data.session);
            } catch (err) {
              // The session is valid; only the profile read failed. Keep it and
              // let the next revalidation pick the profile up.
              console.warn('[auth] profile refresh on focus failed , keeping session', err);
            } finally {
              fetchingProfile.current = false;
            }
          }
          return;
        }

        // No live session. The only thing worth acting on here is the token
        // having disappeared from storage entirely , a failed refresh leaves it
        // in place, and Supabase emits SIGNED_OUT through onAuthStateChange
        // when the session is genuinely over.
        if (cachedUserId && !hasStoredSession()) {
          clearAuthState({ clearQueries: true });
        }
      }, 300);
    };

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      ignore = true;
      clearTimeout(watchdog);
      listener.subscription.unsubscribe();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Auth Functions ----

  const signIn = useCallback(async (email: string, password: string) => {
    storeRef.current.setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };
      if (!data.user || !data.session) {
        return { error: "Sign-in did not return an active session." };
      }

      if (storeRef.current.user?.id && storeRef.current.user.id !== data.user.id) {
        clearAuthState({ clearQueries: true });
      }

      storeRef.current.setUser(data.user);
      storeRef.current.setSession(data.session);
      storeRef.current.setInitialized(true);

      fetchingProfile.current = true;
      try {
        const loadedProfile = await fetchProfile(data.user.id, data.user, data.session);
        if (!loadedProfile) return { error: PROFILE_MISSING_ERROR_MESSAGE };
      } finally {
        fetchingProfile.current = false;
      }

      return {};
    } catch (error) {
      // A connectivity failure after a *successful* credential exchange must not
      // throw the new session away , the user is signed in, we just could not
      // read their profile yet.
      if (isTransientConnectivityError(error)) {
        return {
          error: 'Signed in, but your connection dropped while loading your account. Retrying…',
        };
      }
      await supabase.auth.signOut();
      clearAuthState({ clearQueries: true });
      return { error: getAuthErrorMessage(error, PROFILE_LOAD_ERROR_MESSAGE) };
    } finally {
      storeRef.current.setLoading(false);
    }
  }, [clearAuthState, fetchProfile]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    fullName: string,
    role: "user" | "admin" = "user"
  ) => {
    storeRef.current.setLoading(true);
    try {
      // Created server-side through the admin API so Supabase sends NO email of
      // its own: Menuraq's OTP (/auth/verify-email) is the only verification
      // message. The account stays unconfirmed — and so cannot sign in — until
      // that code is entered. `role` is no longer sent: the server always sets it.
      void role;
      const response = await fetch(resolveApiRequestUrl("/api/auth/signup-account"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, fullName }),
      });
      const body = (await response.json().catch(() => null)) as
        | { ok?: boolean; error?: string; code?: string }
        | null;
      if (!response.ok || !body?.ok) {
        return { error: body?.error || "Signup failed", errorCode: body?.code };
      }

      clearAuthState({ clearQueries: true });

      await fetch(resolveApiRequestUrl("/api/signup"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          fullName,
          provider: "manual",
        }),
      });

      return {};
    } catch (error) {
      // The account and its session already exist by the time we get here; only
      // the profile read or the notification POST failed. A transient failure
      // (the profile deadline expiring, a dropped connection, a 5xx) must not
      // sign out the account the visitor just created , mirroring `signIn`.
      if (isTransientConnectivityError(error)) {
        return {
          error: 'Your account was created, but your connection dropped while loading it. Retrying…',
        };
      }
      await supabase.auth.signOut();
      clearAuthState({ clearQueries: true });
      return { error: getAuthErrorMessage(error, PROFILE_LOAD_ERROR_MESSAGE) };
    } finally {
      storeRef.current.setLoading(false);
    }
  }, [clearAuthState, fetchProfile]);

  const signInWithGoogle = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        // Route OAuth back through the callback handler so PKCE code exchange
        // and post-auth destination resolution run. Sending it straight to the
        // dashboard root skips both. See menuraq-doc/improve/06-google-signin-fix.
        redirectTo:
          typeof window !== "undefined"
            ? authHref("/callback", { absolute: true })
            : undefined,
      },
    });
    if (error) return { error: error.message };
    return {};
  }, []);

  const signInWithApple = useCallback(async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: {
        // Same callback contract as Google: route back through the callback
        // handler so PKCE code exchange and post-auth destination resolution
        // run. Apple is already enabled in the Supabase project (see
        // app/api/auth/oauth/native/route.ts, which relies on it).
        redirectTo:
          typeof window !== "undefined"
            ? authHref("/callback", { absolute: true })
            : undefined,
      },
    });
    if (error) return { error: error.message };
    return {};
  }, []);

  const signOut = useCallback(async () => {
    storeRef.current.setLoading(true);

    // Per-host session cookies (doc 32 D4) mean `supabase.auth.signOut()` clears the
    // cookie on THIS host and nowhere else. Without the call below, signing out on
    // the dashboard would leave a till on pos.menuraq.com transacting — a button
    // that lies is worse than no button.
    //
    // Must run BEFORE the local sign-out, because it authenticates with the session
    // it is about to revoke. And it must never block: a user who clicks "sign out"
    // ends up signed out locally whatever the network did.
    //
    // Note the two `supabase.auth.signOut()` calls elsewhere in this file are
    // deliberately NOT routed here — they are rollbacks after a FAILED sign-in, and
    // globally revoking there would sign the user out of every other device because
    // one profile fetch failed.
    try {
      await fetch("/api/auth/sign-out-everywhere", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
    } catch {
      // Offline or unreachable. Fall through to the local sign-out.
    }

    await supabase.auth.signOut();
    clearAuthState({ clearQueries: true });
    storeRef.current.setLoading(false);
    window.location.href = authHref("/signin");
  }, [clearAuthState]);

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo:
        typeof window !== "undefined"
          ? authHref("/reset-password", { absolute: true })
          : undefined,
    });
    return error ? { error: error.message } : {};
  }, []);

  const updateProfile = useCallback(async (updates: {
    full_name?: string;
    avatar_url?: string;
    role?: string;
  }) => {
    const currentStore = storeRef.current;
    if (!currentStore.user) return { error: "No user logged in" };
    const storeIsOnline = typeof navigator !== "undefined" ? navigator.onLine : true;

    if (
      Object.prototype.hasOwnProperty.call(updates, "avatar_url") &&
      currentStore.profile?.avatar_url &&
      updates.avatar_url !== currentStore.profile.avatar_url
    ) {
      await storage.deleteByPublicUrl("avatars", currentStore.profile.avatar_url);
    }

    if (!storeIsOnline) {
      try {
        const { enqueueAction } = await import('@/lib/offline/action-queue');
        await enqueueAction({
          id: crypto.randomUUID(),
          scope: 'auth',
          type: 'update_profile',
          payload: updates,
          metadata: { userId: currentStore.user.id },
          createdAt: Date.now(),
          maxAttempts: 3,
        });
      } catch (e) { console.error('[AuthContext] failed to send profile update event', e); }
      return {};
    }

    const { error } = await supabase
      .from("profiles")
      .update(updates)
      .eq("id", currentStore.user.id);

    if (error) return { error: error.message };

    try {
      await fetchProfile(currentStore.user.id, currentStore.user, currentStore.session);
    } catch (error) {
      return { error: getAuthErrorMessage(error, PROFILE_LOAD_ERROR_MESSAGE) };
    }

    queryClient.invalidateQueries({ queryKey: ["profile", currentStore.user.id] });
    return {};
  }, [fetchProfile, queryClient]);

  const ensureSession = useCallback(async () => {
    if (storeRef.current.session) {
      return storeRef.current.session;
    }
    const { data } = await supabase.auth.getSession();
    if (data.session?.user) {
      storeRef.current.setSession(data.session);
      storeRef.current.setUser(data.session.user);
    }
    return data.session;
  }, []);

  const value = useMemo<AuthContextProps>(() => ({
    user: store.user,
    session: store.session,
    profile: store.profile,
    loading: store.loading,
    initialized: store.initialized,
    signOut,
    resetPassword,
    updateProfile,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    ensureSession,
  }), [
    store.user,
    store.session,
    store.profile,
    store.loading,
    store.initialized,
    signOut,
    resetPassword,
    updateProfile,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    ensureSession,
  ]);

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
}
